import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Globe, Save, RotateCcw, Check } from 'lucide-react';
import './AdminSEO.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_LABELS = {
  '/': 'Home',
  '/about': 'About',
  '/packages': 'Packages',
  '/gallery': 'Gallery',
  '/blog': 'Blog',
  '/contact': 'Contact'
};

const AdminSEO = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    fetchSEO();
  }, []);

  const fetchSEO = async () => {
    try {
      const response = await axios.get(`${API}/admin/seo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Error fetching SEO:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    setPages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async (page) => {
    setSaving(page.page);
    try {
      await axios.put(`${API}/admin/seo`, {
        page: page.page,
        title: page.title,
        description: page.description
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(page.page);
      setTimeout(() => setSaved(null), 2000);
      fetchSEO();
    } catch (error) {
      alert('Failed to save SEO settings');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (page) => {
    if (!window.confirm(`Reset SEO for "${PAGE_LABELS[page.page]}" to defaults?`)) return;
    try {
      const pagePath = page.page === '/' ? '' : page.page.slice(1);
      await axios.post(`${API}/admin/seo/reset/${pagePath}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSEO();
    } catch (error) {
      alert('Failed to reset SEO');
    }
  };

  if (loading) return <div className="admin-seo"><p>Loading SEO settings...</p></div>;

  return (
    <div className="admin-seo" data-testid="admin-seo-page">
      <div className="seo-header">
        <div>
          <h2>SEO Management</h2>
          <p>Control meta titles and descriptions for each page to improve search engine rankings</p>
        </div>
      </div>

      <div className="seo-info-banner" data-testid="seo-info-banner">
        <Globe size={18} />
        <p>These settings control how your pages appear in Google search results. Keep titles under 60 characters and descriptions under 160 characters for best results.</p>
      </div>

      <div className="seo-pages-list">
        {pages.map((page, index) => {
          const titleLen = (page.title || '').length;
          const descLen = (page.description || '').length;
          const isSaving = saving === page.page;
          const isSaved = saved === page.page;

          return (
            <div key={page.page} className="seo-page-card" data-testid={`seo-card-${page.page.replace('/', '') || 'home'}`}>
              <div className="seo-card-header">
                <div className="seo-page-name">
                  <span className="page-label">{PAGE_LABELS[page.page] || page.page}</span>
                  <span className="page-url">{page.page}</span>
                </div>
                <div className="seo-card-actions">
                  {page.is_custom && (
                    <button
                      onClick={() => handleReset(page)}
                      className="btn-reset"
                      title="Reset to defaults"
                      data-testid={`seo-reset-${page.page.replace('/', '') || 'home'}`}
                    >
                      <RotateCcw size={14} /> Reset
                    </button>
                  )}
                  <button
                    onClick={() => handleSave(page)}
                    className="btn-save"
                    disabled={isSaving}
                    data-testid={`seo-save-${page.page.replace('/', '') || 'home'}`}
                  >
                    {isSaved ? <><Check size={14} /> Saved</> : isSaving ? 'Saving...' : <><Save size={14} /> Save</>}
                  </button>
                </div>
              </div>

              <div className="seo-card-body">
                <div className="seo-field">
                  <label>
                    Meta Title
                    <span className={`char-count ${titleLen > 60 ? 'over' : ''}`}>{titleLen}/60</span>
                  </label>
                  <input
                    type="text"
                    value={page.title}
                    onChange={(e) => handleChange(index, 'title', e.target.value)}
                    placeholder="Enter meta title..."
                    data-testid={`seo-title-${page.page.replace('/', '') || 'home'}`}
                  />
                </div>

                <div className="seo-field">
                  <label>
                    Meta Description
                    <span className={`char-count ${descLen > 160 ? 'over' : ''}`}>{descLen}/160</span>
                  </label>
                  <textarea
                    value={page.description}
                    onChange={(e) => handleChange(index, 'description', e.target.value)}
                    placeholder="Enter meta description..."
                    rows={3}
                    data-testid={`seo-desc-${page.page.replace('/', '') || 'home'}`}
                  />
                </div>

                {/* Google Preview */}
                <div className="seo-preview">
                  <p className="preview-label">Search Preview</p>
                  <div className="google-preview">
                    <div className="preview-title">{page.title || 'Page Title'}</div>
                    <div className="preview-url">{BACKEND_URL?.replace('/api', '')}{page.page}</div>
                    <div className="preview-desc">{page.description || 'No description set.'}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminSEO;
